import { useEventListener } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useSessionStore } from '@Stores';
import { AUTH_SYNC_KEY } from './broadcastAuthAdvance';
import { resolveHomeRoute } from './resolveHomeRoute';

/**
 * NOTE :: Cross-tab reconciler for every PUBLIC auth surface. Mounted once, globally (App.vue).
 * On the dedicated `AUTH_SYNC_KEY` storage signal (see `broadcastAuthAdvance`) it routes the tab to
 * whatever the shared auth state says, so a login / 2FA / reset / logout in one tab pulls the others
 * forward. It reads state and navigates only — it never writes the signal, so there is no echo loop,
 * and the `storage` event only fires in OTHER tabs, so the acting tab is untouched. It runs only on
 * a live signal (never on mount), which preserves the intentional "gap" (opening `/login` from a 2FA
 * wait view is not bounced) and keeps a freshly opened public page safe on load.
 */
export default function useAuthTabSync() {
  const { isInvalidSession, isLogged, isSU, pendingPasswordExpired, pendingTwoFa, pendingTwoFaReadiness, shelterProfile, userProfile } =
    storeToRefs(useSessionStore());

  const route = useRoute();
  const { push } = useRouter();

  useEventListener(window, 'storage', onStorageAuthSync);

  // HELPERS
  function computeAuthTargetRoute(): string {
    if (isInvalidSession.value) return '/login';

    if (String(pendingPasswordExpired.value?.email || '').trim()) return '/password-expired';

    if (pendingTwoFa.value?.challenge_id) return '/2fa/challenge';

    if (pendingTwoFaReadiness.value) return '/2fa/readiness';

    if (isLogged.value) {
      return resolveHomeRoute({
        isSU: isSU.value,
        userProfile: userProfile.value,
        shelterProfile: shelterProfile.value,
      });
    }

    return '/login';
  }

  // EVENTS
  function onStorageAuthSync(event: StorageEvent) {
    const isAuthSyncSignal = event.key === AUTH_SYNC_KEY && !!event.newValue;
    if (!isAuthSyncSignal) return;

    if (!route.meta?.isPublic) return;

    const targetRoute = computeAuthTargetRoute();
    if (targetRoute === route.path) return;

    push(targetRoute).catch(() => {
      // NOTE :: Swallow redundant-navigation errors; another guard may resolve the same target.
    });
  }

  return { onStorageAuthSync };
}
